/**
 * useOrganizationManagement Hook
 *
 * Manages organization and installation switching operations:
 * - Organization switching with installation selection
 * - Installation switching (same org and cross-org)
 * - Modal handlers for organization/installation management
 * - Integration with OrgInstallContext for data refresh
 */

import { useCallback } from "react";
import logger from "../utils/logger";

export function useOrganizationManagement({
  organizationId,
  organizations,
  onSwitchOrganization,
  onSwitchInstallation,
  orgInstallContext,
}) {
  const handleInstallationsChanged = useCallback(async () => {
    logger.log(
      `[handleInstallationsChanged] Installations changed, refreshing list`
    );
    // Trigger a refresh of the installations list in OrgInstallContext
    if (orgInstallContext?.refreshInstallations) {
      await orgInstallContext.refreshInstallations();
    }
  }, [orgInstallContext]);

  const handleOrganizationsChanged = useCallback(async () => {
    logger.log(
      `[handleOrganizationsChanged] Organizations changed, refreshing list`
    );
    // Trigger a refresh of the organizations list in OrgInstallContext
    if (orgInstallContext?.refreshOrganizations) {
      await orgInstallContext.refreshOrganizations();
    }
  }, [orgInstallContext]);

  const handleSwitchInstallation = useCallback(
    (installation) => {
      logger.log(
        `[handleSwitchInstallation] Switching installation:`,
        installation
      );
      // If installation is null or undefined, just trigger the change installation screen
      if (!installation) {
        if (orgInstallContext?.changeInstallation) {
          orgInstallContext.changeInstallation();
        }
        return;
      }

      // Check if the installation belongs to a different organization
      if (installation.orgId && installation.orgId !== organizationId) {
        logger.log(
          `[handleSwitchInstallation] Installation belongs to different org (${installation.orgId}), switching org first`
        );
        // Find the organization object
        const targetOrg = organizations.find(
          (org) => org.orgId === installation.orgId
        );
        if (targetOrg && onSwitchOrganization) {
          logger.log(
            `[handleSwitchInstallation] Found target org: ${targetOrg.name}, switching now`
          );
          // Switch to the organization first (this will trigger the installation selector)
          // Store the installation we want to switch to
          sessionStorage.setItem(
            "pendingInstallationSwitch",
            JSON.stringify({
              installationId: installation.installationId,
              installationName: installation.name,
            })
          );
          onSwitchOrganization(targetOrg);
          return;
        }
      }

      // Switch to the specific installation (same organization)
      if (onSwitchInstallation) {
        onSwitchInstallation(installation);
      }
    },
    [
      onSwitchInstallation,
      onSwitchOrganization,
      organizationId,
      organizations,
      orgInstallContext,
    ]
  );

  const handleSwitchOrganization = useCallback(
    (organization) => {
      logger.log(
        `[handleSwitchOrganization] Switching organization:`,
        organization
      );
      // Switch to the specific organization (will show installation selector)
      if (onSwitchOrganization) {
        onSwitchOrganization(organization);
      }
    },
    [onSwitchOrganization]
  );

  return {
    handleInstallationsChanged,
    handleOrganizationsChanged,
    handleSwitchInstallation,
    handleSwitchOrganization,
  };
}
